import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { t } from '../../i18n/nl.js';
import { fmtDate } from '../../utils/formatUtils.js';
import { toAmsterdamDate, toAmsterdamTime } from '../../utils/dateUtils.js';
import { getActiveClientMemberships, primaryMembershipCredits, membershipDisplayName } from '../../utils/membershipUtils.js';

function fmtLesDateTime(isoString) {
  if (!isoString) return '';
  try {
    const d = toAmsterdamDate(isoString);
    const tijd = toAmsterdamTime(isoString);
    return format(d, 'EEE d MMM', { locale: nl }) + ', ' + tijd;
  } catch {
    return '';
  }
}

function fmtCreditReden(tx) {
  const label = t('creditReasons.' + tx.reason) !== 'creditReasons.' + tx.reason
    ? t('creditReasons.' + tx.reason)
    : tx.reason;
  if (tx.group_lessons?.name) {
    const dateStr = tx.group_lessons.starts_at ? ' — ' + fmtLesDateTime(tx.group_lessons.starts_at) : '';
    return label + ' — ' + tx.group_lessons.name + dateStr;
  }
  return label;
}

export default function ClientCreditsPage() {
  const { user } = useAuth();
  const [memberships, setMemberships] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      try {
        const [membershipData, historyRes] = await Promise.all([
          getActiveClientMemberships(user.id),
          supabase
            .from('credit_transactions')
            .select('id, delta, reason, note, created_at, group_lessons(name, starts_at)')
            .eq('client_id', user.id)
            .order('created_at', { ascending: false }),
        ]);

        if (historyRes.error) throw historyRes.error;

        setMemberships(membershipData);
        setHistory(historyRes.data || []);
      } catch (err) {
        setError(t('mijnCredits.foutLaden') + err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [user?.id]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('mijnCredits.title')}</h1>
        </div>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <>
          {/* Huidig saldo — één kaart per actief abonnement */}
          {memberships.length === 0 ? (
            <div className="card" style={{ marginBottom: 24, textAlign: 'center', padding: '32px 24px' }}>
              <p className="text-muted">{t('mijnCredits.geenLidmaatschap')}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
              {memberships.map(m => (
                <div key={m.id} className="card" style={{ textAlign: 'center', padding: '32px 24px' }}>
                  <div style={{ fontSize: 64, fontWeight: 800, color: 'var(--color-primary)', lineHeight: 1 }}>
                    {primaryMembershipCredits(m)}
                  </div>
                  <div style={{ fontSize: 15, color: 'var(--color-text-muted)', marginTop: 8 }}>
                    {t('mijnCredits.balans')}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 4 }}>
                    {membershipDisplayName(m, t('mijnAbonnement.onbekend'))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Creditgeschiedenis */}
          <div className="card">
            <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>
              {t('mijnCredits.geschiedenisTitel')}
            </h2>
            {history.length === 0 ? (
              <p className="text-muted">{t('mijnCredits.geenGeschiedenis')}</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t('mijnCredits.colDatum')}</th>
                      <th>{t('mijnCredits.colReden')}</th>
                      <th style={{ textAlign: 'right' }}>{t('mijnCredits.colAantal')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(tx => (
                      <tr key={tx.id}>
                        <td className="text-muted">{fmtDate(tx.created_at)}</td>
                        <td>
                          <div>{fmtCreditReden(tx)}</div>
                          {tx.note && (
                            <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{tx.note}</div>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <span style={{
                            fontWeight: 700,
                            color: tx.delta > 0 ? 'var(--color-primary)' : 'var(--color-danger)',
                          }}>
                            {tx.delta > 0 ? '+' : ''}{tx.delta}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
